import { DataGrid, GridRowsProp, GridColDef, GridCellParams, GridValueFormatterParams, GridCallbackDetails } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { useQueryTransactions } from "../../services/use-allocation-api";
import { QueryTransactionsOptions, QueryTransactionsResponse } from "domains/allocations/services/allocation-service";
import moment from "moment";
import localisations from "../../localisation";
import RoutePaths from "../../../../routes/route-paths";
import React from "react";

type AllocationsTableProps = {
    partitionId: string;
    productFormatter?: ProductFormatter;
    partitionFormatter?: PartitionFormatter;
};

function GetRows(response: QueryTransactionsResponse) {
    let toReturn = response.items
        .map((item, index) => {
            return {
                id: index,
                ...item,
            };
        })
        .sort((a, b) => (b.createdDateTime as any) - (a.createdDateTime as any)) as GridRowsProp;

    return toReturn;
}

function GetColumns(productFormatter?: ProductFormatter, partitionFormatter?: PartitionFormatter) {
    let {
        columns: { product, quantityDelta, transactionType, datetime, origin, destination, user },
    } = localisations.allocationHistoryTable;

    let toReturn = [
        {
            field: "productId",
            headerName: product.headerName,
            description: product.description,
            sortable: false,
            filterable: false,
            flex: 0.2,
            renderCell: (params: GridCellParams) =>
                productFormatter ? productFormatter(params.value as number) : undefined,
        },
        {
            field: "quantityChange",
            headerName: quantityDelta.headerName,
            description: quantityDelta.description,
            sortable: false,
            filterable: false,
            flex: 0.09,
        },
        {
            field: "transactionTypeId",
            headerName: transactionType.headerName,
            description: transactionType.description,
            sortable: false,
            filterable: false,
            flex: 0.1,
            renderCell: (params: GridCellParams) => mapTransactionType(params.value as number),
        },
        {
            field: "createdDateTime",
            headerName: datetime.headerName,
            description: datetime.description,
            type: "date",
            sortable: false,
            filterable: false,
            flex: 0.11,
            valueFormatter: (params: GridValueFormatterParams) =>
                moment(params.value as string).format("DD/MM/YY HH:mm"),
        },
        {
            field: "originId",
            headerName: origin.headerName,
            description: origin.description,
            sortable: false,
            filterable: false,
            flex: 0.2,
            renderCell: (params: GridCellParams) => {
                return partitionFormatter && params.value ? ( <Link className="noLink" to={RoutePaths.ShowPartition.replace(":id", params.value as string)}>{partitionFormatter(params.value as guid)}</Link> ) : undefined;
            },
        },
        {
            field: "destinationId",
            headerName: destination.headerName,
            description: destination.description,
            sortable: false,
            filterable: false,
            flex: 0.2,
            renderCell: (params: GridCellParams) => {                
                return partitionFormatter && params.value ? ( <Link className="noLink" to={RoutePaths.ShowPartition.replace(":id", params.value as string)}>{partitionFormatter(params.value as guid)}</Link> ) : undefined;
            },
        },
        {
            field: "userId",
            headerName: user.headerName,
            description: user.description,
            sortable: false,
            filterable: false,
            flex: 0.1,
        },
    ] as GridColDef[];

    return toReturn;
}

function mapTransactionType(transactionTypeId: number): string {
    let typeName = localisations.allocationHistoryTable.transactionTypes[transactionTypeId - 1];
    return typeName === undefined ? `Unknown (${transactionTypeId})` : typeName;
}

function AllocationHistory(props: AllocationsTableProps) {
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(100);
    let options = { partitionId: props.partitionId,  itemsPerPage: pageSize, PageToken: (page + 1).toString()} as QueryTransactionsOptions;
    const { loading, data } = useQueryTransactions(options);

    const handlePageChange = (pageNumber: number, _: GridCallbackDetails) => {
        if (page !== pageNumber) {
            setPage(pageNumber);
        }
    };

    const handlePageSizeChange = (pageSizeNumber: number, _: GridCallbackDetails) => {
        if (pageSize !== pageSizeNumber) {
            setPage(0);
            setPageSize(pageSizeNumber);            
        }
    };


    return (
        <div style={{ height: 500, width: "100%" }}>
            {data && (
                <DataGrid
                    page={page}
                    pageSize={pageSize}
                    rowCount={data.totalCount}
                    paginationMode="server" 
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    rowsPerPageOptions={[25, 50, 100]}
                    rows={GetRows(data)}
                    columns={GetColumns(props.productFormatter, props.partitionFormatter)}
                    loading={loading}
                    disableSelectionOnClick={true}
                />
            )}
        </div>
    );
}

export default AllocationHistory;
